import { useState } from 'react';

const HOST_LASS = 'https://api.lexis.com.ec/api/v1/laas';

interface FormData {
  name: string;
  lastName: string;
  email: string;
  phone: string;
  text: string;
  termsAccepted: boolean;
}

export default function ContactForm() {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    lastName: '',
    email: '',
    phone: '',
    text: '',
    termsAccepted: false
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isLoading) return;
    
    // Reset states
    setError('');
    setShowSuccess(false);
    
    // Basic validation
    if (!formData.name || !formData.lastName || !formData.email || !formData.phone) {
      setError('Por favor, completa todos los campos obligatorios');
      return;
    }
    
    if (!formData.termsAccepted) {
      setError('Por favor, acepta los Términos y condiciones para continuar.');
      return;
    }
    
    setIsLoading(true);
    
    try {
      const response = await fetch(`${HOST_LASS}/zoho/save_lead`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...formData, origen: 'Ayuda' }),
      });
      
      if (!response.ok) {
        throw new Error('Error al enviar la información');
      }
      
      // Success - clear form and show success message
      setFormData({
        name: '',
        lastName: '',
        email: '',
        phone: '',
        text: '',
        termsAccepted: false
      });
      setShowSuccess(true);
      
      // Hide success message after 5 seconds
      setTimeout(() => setShowSuccess(false), 5000);
      
    } catch (err) {
      setError('Error al enviar la información. Por favor, inténtalo de nuevo.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="text-white">
      {/* Título */}
      <h3 className="text-lg md:text-xl text-neutral-200 leading-relaxed mb-6">
        Nuestro equipo te contactará para una asesoría gratuita y sin compromiso.
      </h3>

      {/* Success Message */}
      {showSuccess && (
        <div className="mb-4 p-4 bg-green-500/10 border border-green-500/50 rounded-lg text-green-300">
          ¡Información recibida correctamente! Te contactaremos pronto.
        </div>
      )}
      
      {/* Error Message */}
      {error && (
        <div className="mb-4 p-4 bg-red-500/10 border border-red-500/60 rounded-lg text-red-300">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Nombre */}
        <div className="space-y-2">
          <label htmlFor="name" className="text-sm text-neutral-200">Nombre completo</label>
          <input
            id="name"
            type="text"
            name="name"
            placeholder="Ej. Juan"
            value={formData.name}
            onChange={handleInputChange}
            disabled={isLoading}
            className="w-full rounded-lg bg-transparent text-white placeholder-neutral-400
                       border border-white/20 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400/60
                       disabled:opacity-50 disabled:cursor-not-allowed"
            required
          />
        </div>

        {/* Apellidos */}
        <div className="space-y-2">
          <label htmlFor="lastName" className="text-sm text-neutral-200">Apellidos</label>
          <input
            id="lastName"
            type="text"
            name="lastName"
            placeholder="Ej. García López"
            value={formData.lastName}
            onChange={handleInputChange}
            disabled={isLoading}
            className="w-full rounded-lg bg-transparent text-white placeholder-neutral-400
                       border border-white/20 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400/60
                       disabled:opacity-50 disabled:cursor-not-allowed"
            required
          />
        </div>

        {/* Email */}
        <div className="space-y-2">
          <label htmlFor="email" className="text-sm text-neutral-200">Email</label>
          <input
            id="email"
            type="email"
            name="email"
            placeholder="Ej. juanperez@email.com"
            value={formData.email}
            onChange={handleInputChange}
            disabled={isLoading}
            className="w-full rounded-lg bg-transparent text-white placeholder-neutral-400
                       border border-white/20 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400/60
                       disabled:opacity-50 disabled:cursor-not-allowed"
            required
          />
        </div>

        {/* Teléfono */}
        <div className="space-y-2">
          <label htmlFor="phone" className="text-sm text-neutral-200">Número de celular</label>
          <input
            id="phone"
            type="tel"
            name="phone"
            placeholder="Ej. 09 1234 5678"
            value={formData.phone}
            onChange={handleInputChange}
            disabled={isLoading}
            className="w-full rounded-lg bg-transparent text-white placeholder-neutral-400
                       border border-white/20 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400/60
                       disabled:opacity-50 disabled:cursor-not-allowed"
            required
          />
        </div>

        {/* Mensaje */}
        <div className="space-y-2">
          <label htmlFor="text" className="text-sm text-neutral-200">Escribe aquí tu solicitud</label>
          <textarea
            id="text"
            name="text"
            placeholder="Ej. Necesito más información sobre planes y precios"
            rows={4}
            value={formData.text}
            onChange={handleInputChange}
            disabled={isLoading}
            className="w-full rounded-lg bg-transparent text-white placeholder-neutral-400
                       border border-white/20 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400/60
                       disabled:opacity-50 disabled:cursor-not-allowed"
          />
        </div>

        {/* Términos y condiciones */}
        <div className="flex items-start gap-3 mt-2">
          <input
            type="checkbox"
            name="termsAccepted"
            id="termsAccepted"
            checked={formData.termsAccepted}
            onChange={handleInputChange}
            disabled={isLoading}
            className="mt-1 h-4 w-4 bg-transparent border border-white/60 rounded
                       focus:ring-cyan-400 focus:ring-2 disabled:opacity-50"
            required
          />
          <label htmlFor="termsAccepted" className="text-neutral-200 text-sm leading-relaxed">
            <span>Acepto </span>
            <a 
              href="https://s3.amazonaws.com/lexis.mobile.apps/lexisfinder_mobile/documentos/Acuerdo+de+Uso+Final.pdf" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-cyan-300 underline underline-offset-4 hover:text-cyan-200 transition-colors"
            >
              términos y condiciones
            </a>
          </label>
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="mt-4 w-full rounded-lg bg-[#1A8A98] text-white font-medium px-6 py-3
                     transition-colors duration-200 hover:bg-[#167a87]
                     disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? 'Enviando...' : 'Enviar'}
        </button>
      </form>
    </div>
  );
}
